import PercentRounded from '@mui/icons-material/PercentRounded';
import SavingsOutlined from '@mui/icons-material/SavingsOutlined';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, unwrapJson } from '../../../api/client';
import { EmptyState } from '../../../components/states';
import { moneyCompact } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { PeriodBars, RankingBars } from '../components/charts';
import { ReportFrame } from '../components/ReportFrame';
import { ReportTable } from '../components/ReportTable';
import { Panel, QueryError, Segmented, StatGrid, StatTile } from '../components/ui';
import { useReportParams, usePeriodText } from '../components/useReportParams';
import { fetchCsv, reportFileName } from '../lib/csv';
import { periodLabel } from '../lib/dateRange';
import { num } from '../lib/numbers';
import { ChartRow, ItemCell, M, Margin, Q, RangeControls } from './common';

const emptyState = (
  <EmptyState
    icon={<SavingsOutlined />}
    title="No profit figures for this period"
    body="Gross profit appears once sales invoices are posted in this period."
  />
);

const pct = (v: string | null | undefined) =>
  v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`;

export function GrossProfitByPeriodReport() {
  const p = useReportParams('30d');
  const { format } = useShop();
  const query = { ...p.range, interval: p.interval };
  const q = useQuery({
    queryKey: ['reports', 'gross-profit-by-period', query],
    queryFn: () => unwrapJson(api.GET('/reports/gross-profit/by-period', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.data;
  const t = q.data?.totals;
  const labels = (rows ?? []).map((r) => periodLabel(p.interval, r.periodStart, format.locale));
  return (
    <ReportFrame
      slug="gross-profit-by-period"
      period={usePeriodText(p)}
      controls={<RangeControls p={p} interval />}
      onExport={() =>
        fetchCsv(
          api.GET('/reports/gross-profit/by-period', {
            params: { query: { ...query, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName(`gross-profit-by-${p.interval}`, p.range),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Net revenue"
          value={t && moneyCompact(t.netRevenue, format)}
          hint={t && `Sales ${moneyCompact(t.revenue, format)}`}
          icon={<TrendingUpRounded />}
          loading={q.isLoading}
        />
        <StatTile
          label="Returns"
          value={t && moneyCompact(t.returns, format)}
          icon={<UndoRounded />}
          tone="warning"
          loading={q.isLoading}
        />
        <StatTile
          label="Gross profit"
          value={t && moneyCompact(t.grossProfit, format)}
          hint={t && `Cost of goods ${moneyCompact(t.cogs, format)}`}
          icon={<SavingsOutlined />}
          tone="success"
          loading={q.isLoading}
        />
        <StatTile
          label="Margin"
          value={t && pct(t.marginPercent)}
          hint="Gross profit ÷ net revenue"
          icon={<PercentRounded />}
          tone="info"
          loading={q.isLoading}
        />
      </StatGrid>
      <Panel
        title="Revenue, cost and profit"
        subtitle="Per period, from posted journal entries"
        sx={{ mb: 2.5 }}
      >
        <PeriodBars
          loading={q.isLoading}
          labels={labels}
          series={[
            {
              key: 'netRevenue',
              label: 'Net revenue',
              data: (rows ?? []).map((r) => num(r.netRevenue)),
            },
            { key: 'cogs', label: 'Cost of goods', data: (rows ?? []).map((r) => num(r.cogs)) },
            {
              key: 'grossProfit',
              label: 'Gross profit',
              data: (rows ?? []).map((r) => num(r.grossProfit)),
            },
          ]}
        />
      </Panel>
      <ReportTable
        loading={q.isLoading}
        rows={
          rows
            ? rows
                .filter((r) => num(r.revenue) !== 0 || num(r.cogs) !== 0 || num(r.returns) !== 0)
                .reverse()
            : undefined
        }
        rowKey={(r) => r.periodStart}
        empty={emptyState}
        columns={[
          {
            key: 'period',
            header: 'Period',
            render: (r) => periodLabel(p.interval, r.periodStart, format.locale, true),
          },
          {
            key: 'revenue',
            header: 'Sales',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.revenue} />,
            footer: <M v={t?.revenue} />,
          },
          {
            key: 'returns',
            header: 'Returns',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.returns} />,
            footer: <M v={t?.returns} />,
          },
          {
            key: 'net',
            header: 'Net revenue',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => <M v={r.netRevenue} />,
            footer: <M v={t?.netRevenue} />,
          },
          {
            key: 'cogs',
            header: 'Cost of goods',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => <M v={r.cogs} />,
            footer: <M v={t?.cogs} />,
          },
          {
            key: 'gp',
            header: 'Gross profit',
            align: 'right',
            render: (r) => <M v={r.grossProfit} strong signed />,
            footer: <M v={t?.grossProfit} signed />,
          },
          {
            key: 'margin',
            header: 'Margin',
            align: 'right',
            render: (r) => <Margin value={r.marginPercent} />,
            footer: <Margin value={t?.marginPercent} />,
          },
        ]}
      />
    </ReportFrame>
  );
}

export function GrossProfitByProductReport() {
  const p = useReportParams('thisMonth');
  const { format } = useShop();
  const groupBy = p.get('groupBy') === 'product' ? 'product' : 'variant';
  const query = { ...p.range, groupBy, page: p.page, pageSize: p.pageSize } as const;
  const q = useQuery({
    queryKey: ['reports', 'gross-profit-by-product', query],
    queryFn: () => unwrapJson(api.GET('/reports/gross-profit/by-product', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.data;
  const t = q.data?.totals;
  const firstPage = p.page === 1 ? (rows ?? []) : [];
  const byMargin = [...firstPage]
    .filter((r) => r.marginPercent !== null && num(r.revenue) > 0)
    .sort((a, b) => num(b.marginPercent) - num(a.marginPercent));
  return (
    <ReportFrame
      slug="gross-profit-by-product"
      period={usePeriodText(p)}
      controls={
        <>
          <RangeControls p={p} />
          <Segmented
            label="Group by"
            value={groupBy}
            options={[
              { value: 'variant', label: 'Per variant' },
              { value: 'product', label: 'Per product' },
            ]}
            onChange={(v) => p.update({ groupBy: v === 'variant' ? null : v })}
          />
        </>
      }
      onExport={() =>
        fetchCsv(
          api.GET('/reports/gross-profit/by-product', {
            params: { query: { ...p.range, groupBy, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('gross-profit-by-product', p.range),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Revenue"
          value={t && moneyCompact(t.revenue, format)}
          icon={<TrendingUpRounded />}
          loading={q.isLoading}
        />
        <StatTile
          label="Cost of goods"
          value={t && moneyCompact(t.cogs, format)}
          icon={<UndoRounded />}
          tone="warning"
          loading={q.isLoading}
        />
        <StatTile
          label="Gross profit"
          value={t && moneyCompact(t.grossProfit, format)}
          hint={t && `Margin ${pct(t.marginPercent)}`}
          icon={<SavingsOutlined />}
          tone="success"
          loading={q.isLoading}
        />
      </StatGrid>
      <ChartRow ratio="1fr 1fr">
        <Panel title="Most profitable" subtitle="By gross profit">
          <RankingBars
            loading={q.isLoading}
            label="Gross profit"
            items={firstPage.map((r) => ({ label: r.name, value: num(r.grossProfit) }))}
          />
        </Panel>
        <Panel title="Best margins" subtitle="Gross profit as a share of revenue">
          <RankingBars
            loading={q.isLoading}
            kind="percent"
            label="Margin"
            items={byMargin.map((r) => ({
              label: r.name,
              value: Math.round(num(r.marginPercent) * 10) / 10,
            }))}
          />
        </Panel>
      </ChartRow>
      <ReportTable
        loading={q.isLoading}
        rows={rows}
        rowKey={(r) => r.variantId ?? r.productId}
        empty={emptyState}
        page={q.data?.page}
        onPageChange={p.setPage}
        onPageSizeChange={p.setPageSize}
        columns={[
          { key: 'name', header: 'Item', render: (r) => <ItemCell name={r.name} sku={r.sku} /> },
          {
            key: 'qty',
            header: 'Qty sold',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => <Q v={r.qtySold} unit={r.unitSymbol} />,
          },
          {
            key: 'revenue',
            header: 'Revenue',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.revenue} />,
            footer: <M v={t?.revenue} />,
          },
          {
            key: 'cogs',
            header: 'Cost of goods',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.cogs} />,
            footer: <M v={t?.cogs} />,
          },
          {
            key: 'gp',
            header: 'Gross profit',
            align: 'right',
            render: (r) => <M v={r.grossProfit} strong signed />,
            footer: <M v={t?.grossProfit} signed />,
          },
          {
            key: 'margin',
            header: 'Margin',
            align: 'right',
            render: (r) => <Margin value={r.marginPercent} />,
            footer: <Margin value={t?.marginPercent} />,
          },
        ]}
      />
    </ReportFrame>
  );
}
